import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Table, TableBody, TableRow } from 'smartsuite-ui';
import useSortable from '../../../shared/hooks/tableHooks';
import TableHeader from './tableCells/TableHeader';
import TableCell from './tableCells/TableCell';
import tableCellRenderTypes from './tableCells/tableCellRenderTypes';
import Legend from '../Legend';
import { getCustomizableTableCsv } from '../../export/exportCsvUtils';
import { useFeatureTokenContext } from '../../../shared/hooks/featureTokensContext';
import { colors } from '../../../shared/constants/constants';

export function initializeDataColorMappings(columnConfigs) {
    const colorPalette = [...colors];

    return columnConfigs?.reduce((result, columnConfig) => {
        if (columnConfig.renderType === tableCellRenderTypes.stackedBar) {
            columnConfig.metricKeys.forEach((metricKey) => {
                const color = colorPalette.shift() ?? 'black';
                // eslint-disable-next-line no-param-reassign
                result[metricKey] = color;
            });
        }

        return result;
    }, {});
}

function initializeTableLegend(legendItems, dataColorMappings) {
    if (!legendItems) {
        return [];
    }

    return legendItems.map((legendItem) => {
        const color =
            legendItem.color ??
            dataColorMappings[legendItem.metricKey] ??
            'black';
        return {
            id: color,
            label: legendItem.label,
            color,
        };
    });
}

/* eslint-disable complexity, react/no-array-index-key */
function CustomizableTable({
    configuration,
    data,
    formatValue,
    legendFormatter,
    metricRequest,
    openLinksInNewTab,
    setDownloadData,
    thresholds,
}) {
    const { isFeatureConditionMet, isFeatureConditionValid } =
        useFeatureTokenContext();
    const [sortObject, setSort, sortedResults] = useSortable(
        data,
        configuration.initialSortObject
    );

    // create function and add test
    const columns = useMemo(
        () =>
            configuration.columns.map((headerRow) =>
                headerRow.filter(
                    (column) =>
                        !isFeatureConditionValid(column.conditionalFeature) ||
                        isFeatureConditionMet(column.conditionalFeature)
                )
            ),
        [configuration.columns, isFeatureConditionMet, isFeatureConditionValid]
    );

    const [dataColorMappings] = useState(
        initializeDataColorMappings(_.last(configuration.columns))
    );

    const [tableLegend] = useState(
        initializeTableLegend(configuration.legend, dataColorMappings)
    );

    useEffect(() => {
        setDownloadData(
            getCustomizableTableCsv(columns, legendFormatter, sortedResults)
        );
    }, [columns, legendFormatter, setDownloadData, sortedResults]);

    const renderHeaderRow = (headerRow, rowIndex) => (
        <TableRow key={`header${rowIndex}`}>
            {headerRow.map((columnConfig, columnIndex) => (
                <TableHeader
                    key={`header${rowIndex}cell${columnIndex}`}
                    columnConfig={columnConfig}
                    columnIndex={columnIndex}
                    legendFormatter={legendFormatter}
                    setSort={setSort}
                    sortObject={sortObject}
                />
            ))}
        </TableRow>
    );

    const renderDataRow = (resultRow, rowIndex) => {
        const columnConfigs = _.last(columns);

        return (
            <TableRow key={`row${rowIndex}`}>
                {columnConfigs.map((columnConfig, columnIndex) => (
                    <TableCell
                        key={`row${rowIndex}cell${columnIndex}`}
                        columnConfig={columnConfig}
                        formatValue={formatValue}
                        legendFormatter={legendFormatter}
                        metricRequest={metricRequest}
                        openLinksInNewTab={openLinksInNewTab}
                        dataColorMappings={dataColorMappings}
                        resultRow={resultRow}
                        thresholds={thresholds}
                    />
                ))}
            </TableRow>
        );
    };

    return (
        <div className="widget__table-container">
            <Legend legendItems={tableLegend} />
            <Table cssClassModifiers={['customizable-table']}>
                <thead>{columns.map(renderHeaderRow)}</thead>
                <TableBody>{sortedResults.map(renderDataRow)}</TableBody>
            </Table>
        </div>
    );
}

CustomizableTable.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    data: PropTypes.instanceOf(Array).isRequired,
    formatValue: PropTypes.func.isRequired,
    legendFormatter: PropTypes.func.isRequired,
    metricRequest: PropTypes.instanceOf(Object).isRequired,
    openLinksInNewTab: PropTypes.bool.isRequired,
    setDownloadData: PropTypes.func.isRequired,
    thresholds: PropTypes.instanceOf(Array),
};

CustomizableTable.defaultProps = {
    thresholds: [],
};

export default React.memo(
    CustomizableTable,
    (prevProps, nextProps) =>
        _.isEqual(prevProps.data, nextProps.data) &&
        _.isEqual(prevProps.timeAndPrecision, nextProps.timeAndPrecision) &&
        _.isEqual(prevProps.filterValues, nextProps.filterValues) &&
        _.isEqual(prevProps.whereClauses, nextProps.whereClauses)
);
